import React from "react";

/*
  Logo kaynakları:
  - Kripto: jsdelivr/spothq cryptocurrency-icons CDN
  - Forex: flagcdn.com (ülke bayrakları)
  - Emtia: inline SVG / emoji
*/

// Kripto → CoinGecko simge adı (büyük/küçük harf duyarlı CDN yolu)
const CRYPTO_ICON = {
  BTC:   "btc",
  ETH:   "eth",
  SOL:   "sol",
  BNB:   "bnb",
  XRP:   "xrp",
  ADA:   "ada",
  DOGE:  "doge",
  AVAX:  "avax",
  DOT:   "dot",
  LINK:  "link",
  MATIC: "matic",
  LTC:   "ltc",
  UNI:   "uni",
  ATOM:  "atom",
  TRX:   "trx",
  NEAR:  "near",
  SHIB:  "shib",
  FIL:   "fil",
  APT:   "apt",
  ARB:   "arb",
};

// Forex çift → [base ülke, quote ülke] (flagcdn.com 2-harf ISO kodu)
const FOREX_FLAGS = {
  EURUSD: ["eu", "us"],
  GBPUSD: ["gb", "us"],
  USDJPY: ["us", "jp"],
  USDCHF: ["us", "ch"],
  AUDUSD: ["au", "us"],
  USDCAD: ["us", "ca"],
  EURGBP: ["eu", "gb"],
  NZDUSD: ["nz", "us"],
  EURJPY: ["eu", "jp"],
  GBPJPY: ["gb", "jp"],
  EURCHF: ["eu", "ch"],
  USDZAR: ["us", "za"],
  USDTRY: ["us", "tr"],
  EURAUD: ["eu", "au"],
};

// Emtia → emoji veya renk+harf
const COMMODITY_CONFIG = {
  XAUUSD: { emoji: "🪙", bg: "#b8860b", label: "AU" },
  XAGUSD: { emoji: "🥈", bg: "#888",    label: "AG" },
  WTIUSD: { emoji: "🛢️",  bg: "#4a2e04", label: "OIL" },
  NGAS:   { emoji: "🔥", bg: "#1a4a6e", label: "GAS" },
  COPPER: { emoji: "🪨", bg: "#8B4513", label: "CU" },
  PLAT:   { emoji: "⬜", bg: "#607d8b", label: "PT" },
};

const STOCK_DOMAINS = {
  THYAO: "thy.com",
  GARAN: "garantibbva.com.tr",
  AKBNK: "akbank.com",
  EREGL: "erdemir.com.tr",
  TUPRS: "tupras.com.tr",
  BIMAS: "bim.com.tr",
  KCHOL: "koc.com.tr",
  FROTO: "fordotosan.com.tr",
  SISE: "sisecam.com",
  SAHOL: "sabanci.com",
  ASELS: "aselsan.com.tr",
  SASA: "sasapolyester.com",
  PGSUS: "flypgs.com",
  TOASO: "tofas.com.tr",
  YKBNK: "yapikredi.com.tr",
  ISCTR: "isbank.com.tr",
  PETKM: "petkim.com.tr",
  TCELL: "turkcell.com.tr",
  HEKTS: "hektas.com.tr",
  VESTL: "vestel.com.tr",
};

export default function AssetIcon({ symbol = "", category = "", size = 28 }) {
  const [failedImgs, setFailedImgs] = React.useState({});
  const imgFailed = failedImgs[symbol];

  const markFailed = React.useCallback(() => {
    setFailedImgs((p) => ({ ...p, [symbol]: true }));
  }, [symbol]);

  const ticker = symbol.replace(/USDT$|USD$/, "").toUpperCase();

  // ─── Kripto ───────────────────────────────────────────────────────────
  if (category === "crypto") {
    const slug = CRYPTO_ICON[ticker];
    const src = slug
      ? `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons/32/color/${slug}.png`
      : null;

    if (src && !imgFailed) {
      return (
        <span style={{
          width: size, height: size, borderRadius: "50%",
          overflow: "hidden", display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          background: "var(--iq-panel-hover)", flexShrink: 0,
        }}>
          <img
            src={src}
            alt={ticker}
            width={size}
            height={size}
            onError={markFailed}
            style={{ objectFit: "contain" }}
          />
        </span>
      );
    }
    // fallback
    return (
      <span style={{
        width: size, height: size, borderRadius: "50%",
        background: "rgba(0,230,118,0.15)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: size * 0.35, fontWeight: 700, color: "var(--iq-green)",
        flexShrink: 0,
      }}>
        {ticker.slice(0, 3)}
      </span>
    );
  }

  // ─── Forex ────────────────────────────────────────────────────────────
  if (category === "forex") {
    const flags = FOREX_FLAGS[symbol];
    if (flags && !imgFailed) {
      return (
        <span style={{
          width: size, height: size,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          position: "relative", flexShrink: 0,
        }}>
          {/* Base currency flag (left, slightly behind) */}
          <img
            src={`https://flagcdn.com/20x15/${flags[0]}.png`}
            alt={flags[0]}
            style={{
              width: size * 0.72, height: "auto", borderRadius: 2,
              position: "absolute", left: 0, top: "50%",
              transform: "translateY(-50%)",
              boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
            onError={markFailed}
          />
          {/* Quote currency flag (right, slightly in front) */}
          <img
            src={`https://flagcdn.com/20x15/${flags[1]}.png`}
            alt={flags[1]}
            style={{
              width: size * 0.72, height: "auto", borderRadius: 2,
              position: "absolute", right: 0, top: "50%",
              transform: "translateY(-50%)",
              boxShadow: "0 1px 4px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          />
        </span>
      );
    }
    // fallback
    const base = symbol.slice(0, 3);
    return (
      <span style={{
        width: size, height: size, borderRadius: "50%",
        background: "rgba(33,150,243,0.15)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: size * 0.32, fontWeight: 700, color: "#64b5f6",
        flexShrink: 0,
      }}>
        {base}
      </span>
    );
  }

  // ─── Hisse / Stocks ───────────────────────────────────────────────────
  if (category === "stocks") {
    const domain = STOCK_DOMAINS[symbol];
    const src = domain
      ? `https://www.google.com/s2/favicons?domain=${domain}&sz=${Math.min(size, 64)}`
      : null;
    if (src && !imgFailed) {
      return (
        <span style={{
          width: size, height: size, borderRadius: "50%",
          overflow: "hidden", display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          background: "var(--iq-panel-hover)", flexShrink: 0,
        }}>
          <img
            src={src}
            alt={symbol}
            width={size}
            height={size}
            onError={markFailed}
            style={{ objectFit: "contain" }}
          />
        </span>
      );
    }
    return (
      <span style={{
        width: size, height: size, borderRadius: "50%",
        background: "rgba(156,39,176,0.15)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: size * 0.35, fontWeight: 700, color: "#ce93d8",
        flexShrink: 0,
      }}>
        {symbol.slice(0, 3)}
      </span>
    );
  }

  // ─── Emtia ────────────────────────────────────────────────────────────
  const cfg = COMMODITY_CONFIG[symbol];
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%",
      background: cfg ? cfg.bg + "33" : "rgba(255,152,0,0.15)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.55,
      flexShrink: 0,
    }}>
      {cfg ? cfg.emoji : "📦"}
    </span>
  );
}
